/**
 * CPR Engine (§1, §7, §8, §23, §24)
 *
 * Session aggregation + core CPR / pivot math.
 *
 * - Sessions are calendar groups (UTC day / ISO week / month) over the
 *   loaded candles. On daily/weekly/monthly charts every candle is its own
 *   session, so "previous completed session" = the previous candle.
 * - Levels always come from the session BEFORE the last candle's session
 *   (the last session is treated as "in progress" → non-repainting, §28).
 * - Pure functions only; no candle beyond index i is ever read.
 */

import { CandleData } from "../../../types/chart.types";
import {
  CprCore, CprTimeframe, PivotLevels, PivotType, SessionAgg,
} from "./types";

// ─── Session grouping (§29: works for any intraday timeframe) ─────

/** Calendar group key for a candle time (UTC seconds). */
export const sessionKey = (timeSec: number, tf: CprTimeframe): string => {
  const iso = new Date(timeSec * 1000).toISOString();
  if (tf === "Monthly") return iso.slice(0, 7); // "YYYY-MM"
  if (tf === "Daily") return iso.slice(0, 10);  // "YYYY-MM-DD"
  // Weekly — ISO week (Monday-based, week 1 contains Jan 4)
  const d = new Date(iso.slice(0, 10) + "T00:00:00Z");
  const dayNum = (d.getUTCDay() + 6) % 7;             // Mon = 0
  d.setUTCDate(d.getUTCDate() - dayNum + 3);          // Thursday of this week
  const isoYear = d.getUTCFullYear();
  const firstThu = new Date(Date.UTC(isoYear, 0, 4));
  const fDayNum = (firstThu.getUTCDay() + 6) % 7;
  firstThu.setUTCDate(firstThu.getUTCDate() - fDayNum + 3);
  const week = 1 + Math.round((d.getTime() - firstThu.getTime()) / (7 * 86400000));
  return `${isoYear}-W${String(week).padStart(2, "0")}`;
};

/**
 * Single-pass session aggregation.
 * Returns sessions in chronological order with inclusive candle index ranges.
 */
export const aggregateSessions = (
  candles: CandleData[],
  tf: CprTimeframe = "Daily",
): SessionAgg[] => {
  const out: SessionAgg[] = [];
  let cur: SessionAgg | null = null;
  let curKey = "";

  for (let i = 0; i < candles.length; i++) {
    const c = candles[i];
    const k = sessionKey(c.time, tf);
    if (!cur || k !== curKey) {
      cur = {
        key: k,
        start: c.time,
        end: c.time,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
        volume: c.volume || 0,
        firstIdx: i,
        lastIdx: i,
      };
      out.push(cur);
      curKey = k;
    } else {
      if (c.high > cur.high) cur.high = c.high;
      if (c.low < cur.low) cur.low = c.low;
      cur.close = c.close;
      cur.volume += c.volume || 0;
      cur.end = c.time;
      cur.lastIdx = i;
    }
  }
  return out;
};

// ─── Core CPR calculation (§1, §2) ────────────────────────────────

/**
 * CPR from one session's H/L/C:
 *   Pivot = (H+L+C)/3, BC = (H+L)/2, TC = 2*Pivot - BC,
 *   CPR Top = max(TC,BC), CPR Bottom = min(TC,BC),
 *   Width = Top - Bottom, Width% = Width/Pivot*100.
 */
export const computeCpr = (high: number, low: number, close: number): CprCore => {
  const pivot = (high + low + close) / 3;
  const bc = (high + low) / 2;
  const tc = pivot * 2 - bc;
  const top = Math.max(tc, bc);
  const bottom = Math.min(tc, bc);
  const width = top - bottom;
  const widthPct = pivot !== 0 ? (width / pivot) * 100 : 0;
  return { pivot, bc, tc, top, bottom, width, widthPct };
};

// ─── Pivot ladder (§7, §8) ────────────────────────────────────────

/**
 * Pivot ladder for the selected method. Standard and Classic are identical
 * floor pivots; Fibonacci/Camarilla/Woodie are documented variants.
 * CPR itself always uses the standard pivot (spec §1 formulas).
 */
export const computePivots = (
  type: PivotType,
  high: number,
  low: number,
  close: number,
): PivotLevels => {
  const range = high - low;

  if (type === "Woodie") {
    const pivot = (high + low + 2 * close) / 4;
    return {
      pivot,
      r1: 2 * pivot - low,
      s1: 2 * pivot - high,
      r2: pivot + range,
      s2: pivot - range,
      r3: pivot + 2 * range,
      s3: pivot - 2 * range,
    };
  }

  if (type === "Fibonacci") {
    const pivot = (high + low + close) / 3;
    return {
      pivot,
      r1: pivot + 0.382 * range,
      s1: pivot - 0.382 * range,
      r2: pivot + 0.618 * range,
      s2: pivot - 0.618 * range,
      r3: pivot + 1.0 * range,
      s3: pivot - 1.0 * range,
    };
  }

  if (type === "Camarilla") {
    const pivot = (high + low + close) / 3;
    return {
      pivot,
      r1: close + (range * 1.1) / 12,
      s1: close - (range * 1.1) / 12,
      r2: close + (range * 1.1) / 6,
      s2: close - (range * 1.1) / 6,
      r3: close + (range * 1.1) / 4,
      s3: close - (range * 1.1) / 4,
    };
  }

  // Standard / Classic — floor pivots (§7 formulas)
  const pivot = (high + low + close) / 3;
  return {
    pivot,
    r1: 2 * pivot - low,
    s1: 2 * pivot - high,
    r2: pivot + range,
    s2: pivot - range,
    r3: high + 2 * (pivot - low),
    s3: low - 2 * (high - pivot),
  };
};

// ─── Convenience accessors ────────────────────────────────────────

/** The session before the last candle's session (levels source, §7/§28). */
export const previousCompletedSession = (sessions: SessionAgg[]): SessionAgg | null => {
  if (sessions.length < 2) return null;
  return sessions[sessions.length - 2];
};

/** The last candle's session (treated as the in-progress session). */
export const currentSession = (sessions: SessionAgg[]): SessionAgg | null => {
  if (sessions.length < 1) return null;
  return sessions[sessions.length - 1];
};

/** Previous-day high/low/close (§23). */
export const previousDayLevels = (
  prev: SessionAgg,
): { pdh: number; pdl: number; pdc: number } => ({
  pdh: prev.high,
  pdl: prev.low,
  pdc: prev.close,
});
